import { initializeComponentRegistry } from '@owc/component-registry-connection';
import { addConnectionAemEnvironmentConnection } from '@owc/connection-aem-environment';
import { addTrackingReducerConnection } from '@owc/connection-aem-tracking';
import { addTranslationManagerConnection } from '@owc/connection-aem-translations';
import { addConnectionWkoManager, initializeWkoManager } from '@owc/connection-wko-manager';
import { initializeDealerContext } from '@owc/dealer-context';
import { initializeLogger } from '@seamless/logger';
import {
  ConnectionExistsError,
  initializeStore,
  InvalidConnectionNameError,
  isStoreAvailable,
  PersistentConnection,
  SeamlessConnection,
  SeamlessCoreStore,
} from '@seamless/store';
import { defaultConnections, defaultPackages, defaultStarters } from './config';
import { ConnectionsConfig, InitializerConfig, SetupConfig, Starters } from './types';

/**
 * adds commonly used connections. By default if no config is provided it
 * adds a set of default connections. If you provide a logger instance it
 * will log after each initialization.
 * By default it adds a set number of connections that you can consult
 * in the AvailableConnections type
 *
 * @ignore
 * @param {config} ConnectionsConfig config where you provide the desired connections and
 * logger instance
 * @return {*} {void}
 */
export const addStoreConnections = async (config?: ConnectionsConfig): Promise<void> => {
  const logger = config?.loggerInstance;
  const connectionsToBeAdded = config?.connections ? config.connections : defaultConnections;
  connectionsToBeAdded.forEach(async (connection: any) => {
    switch (connection) {
      case 'AemEnvironment':
        await addConnectionAemEnvironmentConnection();
        logger?.log('added AemEnvironmentConnection');
        break;
      case 'WkoManager':
        await addConnectionWkoManager();
        logger?.log('added ConnectionWkoManager');
        break;
      case 'TranslationManager':
        addTranslationManagerConnection().then(() => {
          logger?.log('added TranslationConnection');
        });
        break;
      case 'Tracking':
        addTrackingReducerConnection().then(() => {
          logger?.log('added TrackingReducerConnection');
        });
        break;
    }
  });
  return;
};

/**
 * Initializes commonly used packages. By default if no config is provided it
 * initializes a set of default packages. If you provide a logger instance it
 * will log after each initialization.
 * By default it initializes a set number of packages that you can consult
 * in the AvailablePackages type
 *
 * @ignore
 * @param {config} InitializerConfig config where you provide the desired packages and
 * logger instace
 * @return {*} {void}
 */
export const initializePackages = (config?: InitializerConfig): void => {
  const logger = config?.loggerInstance;
  const packagesToBeAdded = config?.packages ? config.packages : defaultPackages;
  packagesToBeAdded.forEach(async (packageToAdd: any) => {
    switch (packageToAdd) {
      case 'ComponentRegistry':
        initializeComponentRegistry();
        logger?.log('initialized component registry');
        break;
      case 'DealerContext':
        initializeDealerContext();
        logger?.log('initialized dealerContext');
        break;
      case 'WkoManager':
        initializeWkoManager();
        logger?.log('initialized wko manager');
        break;
    }
  });
  return;
};

/**
 * Setups globals environment. By default if no config is provided it
 * initializes the store. If you provide a string for the logger it also
 * initializes it with the correct identifier.
 * You can also specify if you don't want to initialize one of them in the
 * config.
 *
 * @ignore
 * @param {config} SetupConfig config where you provide the desired starters and
 * namespace (string that identifies the logs)
 * @return {*} {addedStarters} The starters that were initializes
 */
export const setupEnvironment = (config?: SetupConfig) => {
  const addedStarters: Starters = {};
  let startersToBeAdded: string[] = ['SeamlessStore'];
  if (!config?.namespace && config?.starters) {
    startersToBeAdded = config?.starters.filter((starter) => {
      return starter !== 'SeamlessLogger';
    });
  } else if (config?.namespace) {
    startersToBeAdded = config?.starters ? config?.starters : defaultStarters;
  }

  startersToBeAdded.forEach((starter) => {
    switch (starter) {
      case 'SeamlessStore':
        // FIXME: MONKEY PATCH, REMOVE THIS ASAP.
        // YOU DON'T LIKE THIS PIECE OF CODE? SO DON'T WE. WANT US TO REMOVE IT? UPGRADE TO `SEAMLESS STORE v5.0.0` 😎
        // eslint-disable-next-line no-case-declarations
        const store = new SeamlessCoreStore();

        // eslint-disable-next-line no-case-declarations
        const seamlessStoreObject = {
          initializeStore: () => store,
          isStoreAvailable: isStoreAvailable,
          SeamlessConnection: SeamlessConnection,
          PersistentConnection: PersistentConnection,
          ConnectionExistsError: ConnectionExistsError,
          InvalidConnectionNameError: InvalidConnectionNameError,
        };

        if (!window.top?.seamlessStore) {
          (window as any).top!.seamlessStore = new Proxy(seamlessStoreObject, {
            get(target, prop) {
              if (prop === 'store') {
                return new Proxy(
                  {},
                  {
                    get(target, prop) {
                      if ((store as any)[prop]) {
                        return (store as any)[prop];
                      } else if ((store as any).store[prop]) {
                        return (store as any).store[prop];
                      }
                      return Reflect.get(target, prop);
                    },
                  },
                );
              }
              if ((store as any)[prop]) {
                return (store as any)[prop];
              }
              return Reflect.get(target, prop);
            },
          });

          Object.seal(store);

          Object.seal(window.top?.seamlessStore);

          addedStarters.store = store;
        }

        break;
      case 'SeamlessLogger':
        // FIXME: MONKEY PATCH, REMOVE THIS ASAP
        if (config) {
          const logger = initializeLogger(config.namespace);

          const seamlessLoggerInstanceClone = Object.assign(
            Object.create(Object.getPrototypeOf(window.top?.seamlessLogger.seamlessLoggerInstance)),
            window.top?.seamlessLogger.seamlessLoggerInstance,
          );

          Object.assign(window.top!.seamlessLogger, Object.create(Object.getPrototypeOf(seamlessLoggerInstanceClone)));
          Object.setPrototypeOf(window.top!.seamlessLogger, Object.getPrototypeOf(seamlessLoggerInstanceClone));
          Object.assign(window.top!.seamlessLogger, seamlessLoggerInstanceClone);
          addedStarters.logger = logger;
        } else {
          addedStarters.logger = undefined;
        }
        break;
    }
  });
  return addedStarters;
};

export * from './types';
